import React, { useState, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Button } from '../../../components/ui/Button';
import { Label } from '../../../components/ui/Label';
import TextInput from '../../../components/ui/TextInput';
import { cn } from '../../../lib/utils';

export interface SaveAsDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
  suggestedName?: string;
}

const SaveAsDashboardModal: React.FC<SaveAsDashboardModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  suggestedName = '',
}) => {
  const { colors } = useTheme();
  const [name, setName] = useState(suggestedName);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Please enter a dashboard name.');
        return;
      }
      setError(null);
      onConfirm(trimmed);
      setName('');
      onClose();
    },
    [name, onConfirm, onClose]
  );

  const handleClose = useCallback(() => {
    setError(null);
    setName(suggestedName);
    onClose();
  }, [onClose, suggestedName]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        aria-hidden
        onClick={handleClose}
      />
      <div
        className={cn(
          'relative z-10 w-full max-w-md rounded-xl shadow-xl p-6',
          colors.surface,
          'border',
          colors.border
        )}
        role="dialog"
        aria-labelledby="save-as-dashboard-title"
        aria-modal="true"
      >
        <h2 id="save-as-dashboard-title" className={cn('text-lg font-semibold mb-4', colors.text)}>
          Save as new dashboard
        </h2>
        <p className={cn('text-sm mb-4', colors.textMuted)}>
          Save the current layout and widgets as a new named dashboard (e.g. Daily Work, Financial).
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="dashboard-name">Dashboard name</Label>
            <TextInput
              id="dashboard-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="e.g. Financial, Daily Work"
              className="mt-1"
              autoFocus
            />
            {error && (
              <p className={cn('mt-1 text-sm', colors.error, 'bg-transparent')} role="alert">
                {error}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="p-2"
              title="Cancel"
              aria-label="Cancel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </Button>
            <Button type="submit" className="p-2" title="Save dashboard" aria-label="Save dashboard">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveAsDashboardModal;
